import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import { createBrowserRouter, RouterProvider } from 'react-router-dom'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import './i18n'
import './styles/index.css'
import { App } from './App'
import { AppShell } from './components/layout/AppShell'
import { OnboardingRoute } from './routes/onboarding'
import { HomeRoute } from './routes/home'
import { TarotIndexRoute } from './routes/tarot/index'
import { TarotSpreadRoute } from './routes/tarot/spread'
import { TarotCardsRoute } from './routes/tarot/cards'
import { TarotHistoryRoute } from './routes/tarot/history'
import { TarotReadingRoute, TarotSharedRoute } from './routes/tarot/reading'
import { ChatRoute } from './routes/chat'
import { AstroIndexRoute } from './routes/astro/index'
import { AstroNatalRoute } from './routes/astro/natal'
import { AstroNatalFullRoute } from './routes/astro/natal-full'
import { AstroHoroscopeRoute } from './routes/astro/horoscope'
import { AstroWeeklyRoute } from './routes/astro/weekly'
import { AstroRetrogradeRoute } from './routes/astro/retrograde'
import { AstroTransitsRoute } from './routes/astro/transits'
import { LunaIndexRoute } from './routes/luna/index'
import { LunaMonthRoute } from './routes/luna/month'
import { LunaAuspiciousRoute } from './routes/luna/auspicious'
import {
  LunaDreamsRoute,
  LunaDreamReadingRoute,
  LunaDreamsHistoryRoute,
} from './routes/luna/dreams'
import { NumbersIndexRoute } from './routes/numbers/index'
import { NumbersDestinyRoute } from './routes/numbers/destiny'
import { NumbersExpressionRoute } from './routes/numbers/expression'
import { NumbersDayRoute } from './routes/numbers/day'
import { NumbersCompatRoute } from './routes/numbers/compat'
import { CompatIndexRoute } from './routes/compat/index'
import { CompatDetailRoute } from './routes/compat/detail'
import { CompatRedeemRoute } from './routes/compat/redeem'
import { ProfileRoute } from './routes/profile'
import { ProfileBiorhythmsRoute } from './routes/profile/biorhythms'

const queryClient = new QueryClient({
  defaultOptions: {
    queries: { retry: 1, refetchOnWindowFocus: false },
  },
})

const router = createBrowserRouter([
  {
    element: <App />,
    children: [
      { path: '/onboarding', element: <OnboardingRoute /> },
      { path: '/compat/redeem', element: <CompatRedeemRoute /> },
      {
        element: <AppShell />,
        children: [
          { path: '/', element: <HomeRoute /> },
          { path: '/tarot', element: <TarotIndexRoute /> },
          { path: '/tarot/cards', element: <TarotCardsRoute /> },
          { path: '/tarot/history', element: <TarotHistoryRoute /> },
          { path: '/tarot/reading/:id', element: <TarotReadingRoute /> },
          { path: '/tarot/shared/:token', element: <TarotSharedRoute /> },
          { path: '/tarot/:kind', element: <TarotSpreadRoute /> },
          { path: '/chat', element: <ChatRoute /> },
          { path: '/astro', element: <AstroIndexRoute /> },
          { path: '/astro/natal', element: <AstroNatalRoute /> },
          { path: '/astro/natal/full', element: <AstroNatalFullRoute /> },
          { path: '/astro/horoscope', element: <AstroHoroscopeRoute /> },
          { path: '/astro/weekly', element: <AstroWeeklyRoute /> },
          { path: '/astro/retrograde', element: <AstroRetrogradeRoute /> },
          { path: '/astro/transits', element: <AstroTransitsRoute /> },
          { path: '/luna', element: <LunaIndexRoute /> },
          { path: '/luna/month', element: <LunaMonthRoute /> },
          { path: '/luna/auspicious', element: <LunaAuspiciousRoute /> },
          { path: '/luna/dreams', element: <LunaDreamsRoute /> },
          { path: '/luna/dreams/history', element: <LunaDreamsHistoryRoute /> },
          { path: '/luna/dreams/:id', element: <LunaDreamReadingRoute /> },
          { path: '/numbers', element: <NumbersIndexRoute /> },
          { path: '/numbers/destiny', element: <NumbersDestinyRoute /> },
          { path: '/numbers/expression', element: <NumbersExpressionRoute /> },
          { path: '/numbers/day', element: <NumbersDayRoute /> },
          { path: '/numbers/compat', element: <NumbersCompatRoute /> },
          { path: '/compat', element: <CompatIndexRoute /> },
          { path: '/compat/:id', element: <CompatDetailRoute /> },
          { path: '/profile', element: <ProfileRoute /> },
          { path: '/profile/biorhythms', element: <ProfileBiorhythmsRoute /> },
        ],
      },
    ],
  },
])

const rootEl = document.getElementById('root')
if (!rootEl) throw new Error('Root element #root not found')

createRoot(rootEl).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <RouterProvider router={router} />
    </QueryClientProvider>
  </StrictMode>,
)
